<template>
  <div :class="[{ disabled: props.disabled }]">
    <div
      v-if="showMoreLessButtons"
      :class="[{ disabled: topRangeIndicatorDisabled }, 'range-indicator']"
      @click="expandRange(Math.max(props.data.minButtonCount - 2, 2), props.data.maxButtonCount)"
    >
      <i class="mdi mdi-chevron-up"></i>
    </div>

    <table border="1">
      <thead>
        <tr class="header-row">
          <th class="name-cell"></th>
          <th class="data-cell">{{ $t("buttonhole_calculator.Button_count") }}</th>
          <th class="data-cell">{{ $t("buttonhole_calculator.Interval_stitches") }}</th>
          <th class="data-cell">{{ $t("buttonhole_calculator.Total_stitches") }}</th>
          <th class="data-cell rightmost-cell">{{ $t("buttonhole_calculator.Difference") }}</th>
        </tr>
      </thead>
      <tbody>
        <tr class="data-row" v-for="(suggestion, index) in data.suggestions" :key="index">
          <td>
            <div class="name-cell">
              <input
                type="radio"
                :name="'buttonhole-selection'"
                :value="index"
                v-model="internalSelectedSuggestion"
                class="radio-input"
              />
            </div>
          </td>
          <td>
            <div class="data-cell">{{ suggestion.buttonCount }}</div>
          </td>
          <td>
            <div class="data-cell">{{ suggestion.intervalStitchCount }}</div>
          </td>
          <td>
            <div class="data-cell">{{ suggestion.stitchCount }}</div>
          </td>
          <td>
            <div :class="['data-cell', 'rightmost-cell', getDifferenceClass(suggestion)]">
              {{ suggestion.stitchCount - props.data.initialStitchCount }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="bottom-controls">
      <div class="spacer"></div>
      <div
        v-if="showMoreLessButtons"
        :class="[{ disabled: bottomRangeIndicatorDisabled }, 'range-indicator']"
        @click="expandRange(props.data.minButtonCount, props.data.maxButtonCount + 2)"
      >
        <i class="mdi mdi-chevron-down"></i>
      </div>
      <button v-if="showMoreLessButtons" class="reset-button" @click="resetTable">
        {{ $t("buttonhole_calculator.Reset_table") }}
      </button>
    </div>


    <ButtonholeInstructions
      :initial-stitch-count="props.data.initialStitchCount"
      :selected-suggestion="data.suggestions[internalSelectedSuggestion]"
      :rib-pattern-repetition="props.data.ribPatternRepetition"
    />
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import ButtonholeInstructions from "./ButtonholeInstructions.vue";

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  selectedSuggestion: {
    type: Number,
    default: null,
  },
});

const internalSelectedSuggestion = ref(props.selectedSuggestion);

watch(
  () => props.selectedSuggestion,
  (newValue) => {
    internalSelectedSuggestion.value = newValue;
  }
);

watch(internalSelectedSuggestion, (newValue) => {
  emit("update:selected", newValue);
});

const showMoreLessButtons = computed(() => {
  return !props.disabled;
});

const expandRange = (min, max) => {
  emit("expand-range", min, max);
};

const emit = defineEmits(["update:selected", "expand-range"]);

const lowestAbsDifference = computed(() => {
  return Math.min(
    ...props.data.suggestions.map((suggestion) =>
      Math.abs(suggestion.stitchCount - props.data.initialStitchCount)
    )
  );
});

const highestAbsDifference = computed(() => {
  return Math.max(
    ...props.data.suggestions.map((suggestion) =>
      Math.abs(suggestion.stitchCount - props.data.initialStitchCount)
    )
  );
});

const getDifferenceClass = (suggestion) => {
  const diff = suggestion.stitchCount - props.data.initialStitchCount;
  const absDiff = Math.abs(diff);

  if (absDiff === lowestAbsDifference.value) {
    return "lowest-difference";
  } else if (absDiff === highestAbsDifference.value) {
    return "highest-difference";
  }
  return "";
};

const resetTable = () => {
  internalSelectedSuggestion.value = null;
  expandRange(7, 10);
};

const topRangeIndicatorDisabled = computed(() => {
  return props.data.minButtonCount <= 2;
});

const bottomRangeIndicatorDisabled = computed(() => {
  return props.data.maxButtonCount > props.data.suggestions[props.data.suggestions.length - 1].buttonCount;
});
</script>

<style scoped lang="scss">
@import "./../../../scss/styles.scss";

table {
  width: 100%;
  border-spacing: 0 0.62rem;
  border: none;
  table-layout: auto;
}

th {
  padding-right: 0.8rem;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
  padding-top: 0.62rem;
}

td {
  border: none;
}

.header-row th {
  border: none;
}

tbody {
  @include paragraphMediumBold;
  color: var(--knittable-lav-darker);
  width: 100%;
}

.app-sm-and-down tbody {
  @include paragraphSmallBold;
}

.header-row {
  @include eyebrowsSmall;
  color: var(--knittable-lav);
  display: grid;
  grid-template-columns: 0.5fr 1fr 1fr 1fr 1fr;
}

.app-sm-and-down .header-row {
  font-size: 0.65rem;
}

.data-cell,
.name-cell {
  border: 1.5px solid var(--knittable-lav-light);
  padding-right: 0.8rem;
  padding-left: 0.8rem;
  height: 3.5rem;
  display: flex;
  align-items: center;
}

.data-row .data-cell,
.data-row .name-cell {
  background: var(--knittable-lav-lighter);
}

.data-cell {
  border-left: none;
  border-right: none;
  justify-content: center;
}

.name-cell {
  justify-content: center;
  border-right: none;
  border-radius: 999px 0 0 999px;
}

.rightmost-cell {
  padding-right: 1rem;
  border-left: none;
  border-right: 1.5px solid var(--knittable-lav-light);
  border-radius: 0 999px 999px 0;
}

.data-row {
  display: grid;
  grid-template-columns: 0.5fr 1fr 1fr 1fr 1fr;
  margin-bottom: 0.4rem;
}

.radio-input {
  cursor: pointer;
  width: 1.25rem;
  height: 1.25rem;
  margin: 0;
  accent-color: var(--knittable-lav);
}

.header-row th {
  height: 3.5rem;
  display: flex;
  align-items: start;
}

.range-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  margin: 0.25rem auto;
  background: transparent;
  border: 1.5px solid var(--knittable-lav-light);
  border-radius: 50%;
  cursor: pointer;
  color: var(--knittable-lav);
  transition: all 0.2s ease;

  i {
    font-size: 1.25rem;
  }

  &:hover {
    background: var(--knittable-lav-lighter);
    color: var(--knittable-lav-darker);
    border-color: var(--knittable-lav);
  }
}

.lowest-difference {
  color: var(--knittable-green, #2ecc71) !important;
}

.highest-difference {
  color: var(--knittable-red, #e74c3c) !important;
}

.bottom-controls {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1rem;
  margin: 0.25rem 0;
  width: 100%;
}

.reset-button {
  justify-self: end;
  @include paragraphSmallBold;
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1.5px solid var(--knittable-lav-light);
  border-radius: 999px;
  color: var(--knittable-lav);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--knittable-lav-lighter);
    color: var(--knittable-lav-darker);
    border-color: var(--knittable-lav);
  }
}

.disabled {
  opacity: 0.5;
  pointer-events: none;

  .radio-input {
    cursor: not-allowed;
  }

  .range-indicator {
    cursor: not-allowed;
    &:hover {
      background: transparent;
      color: var(--knittable-lav);
      border-color: var(--knittable-lav-light);
    }
  }

  .reset-button {
    cursor: not-allowed;
    &:hover {
      background: transparent;
      color: var(--knittable-lav);
      border-color: var(--knittable-lav-light);
    }
  }
}
</style>
